import React, { Fragment, useState } from 'react';
import { Button, Table, Space, Switch, Tooltip, Typography } from 'antd';
import { EditOutlined, EyeOutlined } from '@ant-design/icons';
import TablePagination from '../TablePagination';
import { Link } from "react-router-dom";
import type { ColumnsType } from "antd/es/table";

import PrescriptionsData from "../../types/PrescriptionsData";
import PrescriptionStatus from "../PrescriptionStatus";
import { formatDateToFullString } from "../../util/date";
import { Prescription } from '../../types/Prescription';
import { operationNames, userHasAccess } from "../../data/pages";
import PrescriptionModal from "./PrescriptionModal";
import { flagPrescription } from "../../api/prescription";
import { useTexts } from "../../hooks/useTexts";

const initialSelected: Prescription = {
    id: 0,
    user: {
        id: 0,
        username: '',
        firstname: '',
        lastname: ''
    },
    product: { id: 0, name: '' },
    mediator: {
        id: 0,
        firstname: '',
        lastname: '',
        phoneNumber: '',
        birthDate: '',
        place: {
            id: 0,
            name: '',
            city: ''
        }
    },
    phoneNumber: '',
    additionalPhoneNumber: '',
    status: null,
    note: '',
    flagged: false,
    createdAt: null,
    updatedAt: null
};

const PrescriptionTable = ({ data, loading, refresh, onPageChange }: {
    data: PrescriptionsData,
    loading: boolean,
    refresh: () => void,
    onPageChange: (page: number, pageSize: number) => void
}): JSX.Element => {
    const { t } = useTexts();
    const [selected, setSelected] = useState<Prescription>(initialSelected);
    const [open, setOpen] = useState(false);
    const canEdit = userHasAccess(operationNames.EDIT_PRESCRIPTION);

    const openModal = () => setOpen(true);
    const closeModal = () => {
        setOpen(false);
        setSelected(initialSelected);
    };

    const handleFLagClick = (id: number, flagged: boolean) => {
        flagPrescription(id, { flagged })
            .then(() => refresh())
            .catch(err => console.error("Error occurred: ", err));
    };

    const columns: ColumnsType<Prescription> = [
        {
            title: 'No',
            key: 'orderNumber',
            render: (_, record, index) => ((data.pagination.page - 1) * data.pagination.pageSize) + index + 1,
            width: 10,
        },
        {
            title: t('prescription', 'product'),
            key: 'product',
            render: (_, record) => record.product?.name
        },
        {
            title: t('prescription', 'place'),
            key: 'place',
            render: (_, record) => `${record.mediator?.place?.name} (${record.mediator?.place?.city})`
        },
        {
            title: t('prescription', 'mediatorFullname'),
            key: 'mediator',
            render: (_, record) => `${record.mediator?.firstname} ${record.mediator?.lastname}`
        },
        {
            title: t('prescription', 'contactNumber'),
            key: 'contact',
            render: (_, record) => (
                <>
                    {record.phoneNumber}
                    {!!record.additionalPhoneNumber && <><br />{record.additionalPhoneNumber}</>}
                </>
            )
        },
        {
            title: t('prescription', 'status'),
            key: 'status',
            render: (_, record) => <PrescriptionStatus status={record.status} />
        },
        {
            title: t('prescription', 'note'),
            key: 'note',
            render: (_, record) => (
                <Tooltip title={record.note}>
                    <span style={{
                        display: '-webkit-box',
                        WebkitLineClamp: 3,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                        maxWidth: '200px',
                        whiteSpace: 'pre-wrap',
                        wordBreak: 'break-word',
                    }}>
                        {record.note}
                    </span>
                </Tooltip>
            )
        },
        {
            title: t('prescription', 'user'),
            key: 'user',
            render: (_, record) => record.user.username
        },
        {
            title: t('prescription', 'createdAt'),
            key: 'createdAt',
            render: (_, record) => record.createdAt ? formatDateToFullString(record.createdAt) : "-"
        },
        {
            title: t('prescription', 'updatedAt'),
            key: 'updatedAt',
            render: (_, record) => record.updatedAt ? formatDateToFullString(record.updatedAt) : "-"
        },
        {
            title: '',
            key: 'index',
            render: (_, record) => (
                <Link to={"/prescriptions/" + record.id} target="_blank" rel="noopener noreferrer">
                    <EyeOutlined style={{ fontSize: '20px', color: '#4F46E5' }} />
                </Link>
            ),
            width: 50,
        },
        {
            title: '',
            key: 'actions',
            render: (_, record) => (
                <Space>
                    {canEdit && (
                        <Button
                            type="primary"
                            icon={<EditOutlined />}
                            onClick={() => {
                                setSelected(record);
                                openModal();
                            }}
                        />
                    )}
                    {userHasAccess(operationNames.FLAG_PRESCRIPTION) && (
                        <Switch
                            checked={record.flagged}
                            onChange={(checked) => handleFLagClick(record.id, checked)}
                            checkedChildren="★"
                            unCheckedChildren="☆"
                        />
                    )}
                </Space>
            )
        }
    ];

    return (
        <Fragment>
            <style>{`
                @media (max-width: 768px) {
                    .prescription-table .ant-table-cell {
                        padding: 6px 6px !important;
                        font-size: 12px;
                    }
                }
            `}</style>

            <PrescriptionModal
                open={open}
                onClose={closeModal}
                successCallback={refresh}
                selected={selected}
            />

            <div style={{ marginBottom: '16px' }}>
                <Typography.Text>{t('prescription', 'totalCount')}: {data.pagination.elementCount}</Typography.Text>
            </div>

            <div style={{ background: 'white', padding: '16px', borderRadius: '8px', boxShadow: '0 2px 8px rgba(0,0,0,0.05)' }}>
                <Table
                    className="prescription-table"
                    columns={columns}
                    dataSource={data.prescriptions}
                    rowKey="id"
                    loading={loading}
                    pagination={false}
                    scroll={{ x: 'max-content' }}
                />
                <TablePagination
                    current={data.pagination.page}
                    pageSize={data.pagination.pageSize}
                    pageCount={data.pagination.pageCount}
                    total={data.pagination.elementCount}
                    onChange={onPageChange}
                />
            </div>
        </Fragment>
    );
};

export default PrescriptionTable;